import React, { useEffect, useRef, useState } from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

import {
  BG_COLORS,
  COLORS,
  CONNECTORS,
  CONNECTORS_STATUS_TRANSLATED,
} from './helpers';

import { getCurrentChargingUserName } from '../../../hooks/chargepoints';
import { hideToolTip } from '../charts/tools';

interface IConnectorColored {
  connector?:
    | string
    | 'Type 1'
    | 'Type 2'
    | 'Tesla'
    | 'CCS Type 2'
    | 'CCS Type 1'
    | 'CHAdeMO'
    | 'GB/T'
    | 'Plug NBR';
  status?: 'available' | 'busy' | 'unavailable' | 'maintenance' | 'faulted' | 'groupFull';
  className?: string;
  socPercentage?: string;
  currentChargingUserName?: string;
  connectorPk?: number;
}

const ConnectorColored: React.FC<IConnectorColored> = ({
  connector = 'Type 2',
  status = 'unavailable',
  socPercentage,
  connectorPk,
}) => {
  const [userName, setUserName] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hovered && !userName && status === 'busy') {
      setUserName('Loading...');

      debounceTimeout.current = setTimeout(async () => {
        const userName = await getCurrentChargingUserName(connectorPk);

        if (userName) {
          setUserName(userName.currentChargingUserName);
        } else {
          setUserName(null);
        }
      }, 1000);
    } else if (!hovered) {
      setUserName(null);
    }

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [hovered]);

  return (
    <Div
      status={status}
      connector={connector}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={(e) => {
        hideToolTip(e);
        setHovered(false);
      }}
      socPercentage={socPercentage}
      currentChargingUserName={userName}
    >
      {CONNECTORS[connector]}
    </Div>
  );
};

export default ConnectorColored;

const content = ({
  connector,
  status,
  socPercentage,
  currentChargingUserName,
}) =>
  `${connector} - ${T(CONNECTORS_STATUS_TRANSLATED[status])}` +
  (socPercentage != null && socPercentage !== ''
    ? ` - ${socPercentage}%`
    : '') +
  (currentChargingUserName ? `\\A ${currentChargingUserName}` : '');

const Div = styled.div<IConnectorColored>`
  position: relative;

  &::after {
    content: '${({
      connector,
      status,
      socPercentage,
      currentChargingUserName,
    }) =>
      content({ connector, status, socPercentage, currentChargingUserName })}';
    position: absolute;
    left: 50%;
    bottom: -12px;
    z-index: 3;
    width: max-content;
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 10px;
    opacity: 0;
    transition: opacity 200ms ease-in-out;
    background-color: ${({ status }) => (status ? BG_COLORS[status] : 'unset')};
    transform: translateX(-65%);
    transform: translateX(-50%);
    white-space: pre;
    text-align: center;
  }
  &:hover::after {
    opacity: 1;
  }

  img {
    filter: ${({ status }) => `drop-shadow(0px 0px 5px ${COLORS[status]})`};
  }
`;
